import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Users, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Plus, ArrowUpRight, ShieldAlert, Briefcase, Home, FileSearch, TrendingUp,
  Archive, ArchiveRestore, DollarSign, User, UserCheck, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import VerificationPipelineStepper from "@/components/VerificationPipelineStepper";
import StageBadge from "@/components/StageBadge";
import HouseholdExecutiveSummaryStripe from "@/components/HouseholdExecutiveSummaryStripe";
import HouseholdRecommendationHero from "@/components/HouseholdRecommendationHero";
import HouseholdFinalDecisionPanel from "@/components/HouseholdFinalDecisionPanel";
import HouseholdRiskIndicatorsList from "@/components/HouseholdRiskIndicatorsList";
import HouseholdOverviewCard from "@/components/HouseholdOverviewCard";
import HouseholdVerificationStatusCard from "@/components/HouseholdVerificationStatusCard";
import HouseholdMembersGrid from "@/components/HouseholdMembersGrid";
import HouseholdOrderDialog from "@/components/HouseholdOrderDialog";
import RiskIntelligenceSummaryCard from "@/components/RiskIntelligenceSummaryCard";
import { householdStageFor } from "@/lib/verifyHub";
import { fmtDate } from "@/lib/dateFormat";

const ROLE_LABEL = { primary: "Primary", co_applicant: "Co-applicant", cosigner: "Cosigner" };
const ROLE_TONE = {
  primary: "bg-[#0A192F] text-white",
  co_applicant: "bg-[#F3EFEA] text-[#0A192F]",
  cosigner: "bg-[#F59E0B]/15 text-[#92400E]",
};

export default function GroupDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    apiClient.get(`/groups/${id}`).then(({ data }) => setG(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  };
  useEffect(load, [id]);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("ntn:notifications:refresh"));
  }, [id]);

  const toggleArchive = async () => {
    if (!g) return;
    const path = g.archived_at ? "unarchive" : "archive";
    try {
      await apiClient.post(`/groups/${g.id}/${path}`);
      toast.success(g.archived_at ? "Household restored" : "Household archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update archive state");
    }
  };

  const [aggregateRisk, setAggregateRisk] = useState(0);
  const [aggregateIndicators, setAggregateIndicators] = useState([]);

  if (error) return <AppShell><div className="text-[#EF4444]">{error}</div></AppShell>;
  if (!g) return <AppShell><div className="text-[#4A5568]">Loading…</div></AppShell>;

  const h = g.household || {};
  const canArchive = user?.role !== "applicant";
  const householdStage = householdStageFor(g);
  const isDecisionReady = householdStage.key === "ready" || householdStage.key === "complete";

  // Household status badge label — mirrors the platform status terminology.
  const statusBadge = householdStage.key === "ready" ? "Decision-Ready Household"
    : householdStage.key === "complete" ? "Decision Recorded"
    : householdStage.key === "additional" ? "Additional Information Needed"
    : householdStage.key === "validation" ? "Verification In Progress"
    : householdStage.key === "verify" ? "Verification In Progress"
    : "In Progress";

  return (
    <AppShell>
      <div className="mb-6">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#4A5568] hover:text-[#0A192F]" data-testid="back-to-dash">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
        </Link>
      </div>

      {g.archived_at && (
        <div className="mb-5 inline-flex items-center gap-2 text-xs font-mono tracking-[0.14em] uppercase bg-[#F3EFEA] border border-[#0A192F]/15 px-3 py-1.5 rounded-sm"
             data-testid="group-archived-banner">
          <Archive className="h-3.5 w-3.5" /> Archived {fmtDate(g.archived_at)}
          {g.archived_by_name && <span>· by {g.archived_by_name}</span>}
        </div>
      )}

      {/* HEADER — Household Name + Property + Address + Status Badge */}
      <div className="mb-6 bg-white rounded-lg ring-1 ring-slate-200 shadow-sm p-6 sm:p-8" data-testid="household-header">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">
              <Users className="h-3 w-3" /> Household Profile
            </div>
            <h1 className="ntn-h1" data-testid="group-name">{g.name}</h1>
            {g.property_address && (
              <div className="text-base text-slate-700 mt-2.5 font-medium" data-testid="household-property-address">{g.property_address}</div>
            )}
            <div className="text-xs text-slate-500 mt-1.5 font-mono">
              {g.id.slice(0, 8).toUpperCase()}
              {g.monthly_rent ? ` · $${Number(g.monthly_rent).toLocaleString()}/mo` : ""}
              {" "}· {(g.member_screenings || []).filter((m) => !m.removed).length} active applicant{(g.member_screenings || []).filter((m) => !m.removed).length === 1 ? "" : "s"}
              {(g.member_screenings || []).some((m) => m.removed)
                ? ` · ${(g.member_screenings || []).filter((m) => m.removed).length} removed`
                : ""}
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <StageBadge stageKey={householdStage.key} />
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ring-1 ring-inset ${
                isDecisionReady ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-blue-50 text-blue-700 ring-blue-200"
              }`} data-testid="household-status-badge">
                {statusBadge}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canArchive && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className={`rounded-md ${g.archived_at ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-300"}`}
                          data-testid="group-archive-btn">
                    {g.archived_at
                      ? <><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</>
                      : <><Archive className="h-4 w-4 mr-2" /> Archive household</>}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {g.archived_at ? "Restore this household?" : "Archive this household?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {g.archived_at
                        ? "The household and every member's screening will reappear on your active dashboard."
                        : "The household card and all member screenings will be hidden from the active dashboard but kept in your archive. You can restore at any time."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={toggleArchive} className="bg-[#0A192F]"
                                       data-testid="group-archive-confirm">
                      {g.archived_at ? "Restore" : "Archive"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {!g.archived_at && (user?.role === "property_manager" || user?.role === "ntn_staff") && (
              <HouseholdOrderDialog group={g} onOrdered={load} />
            )}
            {!g.archived_at && <AddMemberDialog group={g} onChanged={load} />}
          </div>
        </div>
      </div>

      {/* 1. EXECUTIVE SUMMARY — Bloomberg-style household command-center stripe */}
      <div className="mb-6">
        <HouseholdExecutiveSummaryStripe group={g} />
      </div>

      {/* 2. Verify Hub workflow stepper — household-level "slowest member" stage */}
      <div className="mb-6">
        <VerificationPipelineStepper
          currentStageKey={householdStage.key}
          title="Verification Workflow"
          subtitle={`${(g.member_screenings || []).filter((m) => !m.removed).length} active applicant${(g.member_screenings || []).filter((m) => !m.removed).length === 1 ? "" : "s"} · stage reflects slowest member`}
          testid="household-stepper"
        />
      </div>

      {/* ──────────── 2-COLUMN HOUSEHOLD INFORMATION ARCHITECTURE ────────────
          Left  (6/12): Primary Intelligence — NTN Recommendation, Risk
                        Intelligence Summary, Household Risk Indicators.
          Right (6/12): Supporting Household Summary — Household Overview,
                        Verification Status. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 ntn-stagger">
        {/* LEFT COLUMN — Primary Intelligence */}
        <div className="lg:col-span-6 space-y-6" data-testid="household-primary-intelligence">
          {/* 1. NTN Recommendation — intelligence layer (most prominent when
                  the household hasn't been closed yet). */}
          <HouseholdRecommendationHero group={g} isStaff={user?.role === "ntn_staff"} onPosted={load} />

          {/* 2. PM Final Decision — closing-outcome surface. Renders as either
                  (a) an action card (PM, no member decided), (b) a closed
                  banner (uniform final decision recorded), or (c) a blocked
                  notice (some members already individually decided). */}
          <HouseholdFinalDecisionPanel group={g} isPM={user?.role === "property_manager"} onApplied={load} />

          {/* 2. Risk Intelligence Summary — Phase E.1: manual value wins */}
          <RiskIntelligenceSummaryCard
            screeningId={null}
            openCount={aggregateRisk}
            indicators={aggregateIndicators}
            stored={g?.household_risk_summary}
            staffEndpoint={`/groups/${g?.id}/household-risk`}
            onStoredSaved={(v) => load()}
            eyebrow="Risk Intelligence Summary · Household"
          />

          {/* 3. Household Risk Indicators — alert-style cards */}
          <HouseholdRiskIndicatorsList
            group={g}
            onCountChange={setAggregateRisk}
            onIndicatorsChange={setAggregateIndicators}
          />
        </div>

        {/* RIGHT COLUMN — Supporting Household Summary */}
        <div className="lg:col-span-6 space-y-6" data-testid="household-supporting-intelligence">
          <HouseholdOverviewCard group={g} isStaff={user?.role === "ntn_staff"} onUpdated={load} />
          <HouseholdVerificationStatusCard group={g} />
        </div>

        {/* FULL WIDTH — Household Members grid */}
        <div className="lg:col-span-12 mt-2">
          <HouseholdMembersGrid group={g} onChanged={load} />
        </div>
      </div>
    </AppShell>
  );
}

/* ---------------- Household card ---------------- */
// v1.0.7 — legacy HouseholdCard (and its helpers) removed: superseded by
// HouseholdOverviewCard / HouseholdRecommendationHero and never rendered.

// v1.0.8 — dead MemberCard/RemoveMemberButton removed; the live roster is
// HouseholdMembersGrid (staff-only soft removal lives there).

function AddMemberDialog({ group, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    role: "co_applicant",
    applicant_first_name: "", applicant_last_name: "",
    applicant_email: "", applicant_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/groups/${group.id}/members`, {
        ...form,
        applicant_phone: form.applicant_phone || null,
      });
      toast.success(`${ROLE_LABEL[form.role]} added to group`);
      setOpen(false);
      setForm({ role: "co_applicant", applicant_first_name: "", applicant_last_name: "", applicant_email: "", applicant_phone: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Add failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5" data-testid="add-member-btn">
          <Plus className="h-4 w-4 mr-2" /> Add applicant
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Add applicant to {group.name}</DialogTitle>
          <DialogDescription>
            We&apos;ll create a screening record for them under this household. No email is sent to the
            applicant — upload their completed application and documents from their member card, or
            order services once they&apos;re added.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="add-member-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="co_applicant">Co-applicant (lives in the unit)</SelectItem>
                <SelectItem value="cosigner">Cosigner (financially backs the lease)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.applicant_first_name} onChange={(e) => set("applicant_first_name", e.target.value)}
              placeholder="First name" className="h-11 rounded-sm" data-testid="add-member-fname" />
            <Input value={form.applicant_last_name} onChange={(e) => set("applicant_last_name", e.target.value)}
              placeholder="Last name" className="h-11 rounded-sm" data-testid="add-member-lname" />
          </div>
          <Input type="email" value={form.applicant_email} onChange={(e) => set("applicant_email", e.target.value)}
            placeholder="Email" className="h-11 rounded-sm" data-testid="add-member-email" />
          <Input value={form.applicant_phone} onChange={(e) => set("applicant_phone", e.target.value)}
            placeholder="Phone (optional)" className="h-11 rounded-sm" data-testid="add-member-phone" />
        </div>
        <DialogFooter>
          <Button onClick={submit}
            disabled={busy || !form.applicant_first_name || !form.applicant_last_name || !form.applicant_email}
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="add-member-submit">
            {busy ? "Adding…" : "Add applicant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
